import { memo, useMemo } from 'react';
import classnames from 'classnames/bind';
import { Text } from '@wildberries/ui-kit';
import i18next from 'i18next';
import {
  TConvertedCoefficientsTypeReport,
  TSearchMultiInputOptionsArray,
} from '@/pages/supplies-management/children/warehouses-limits/redux/warehouses-limits-module/types';
import { WAREHOUSES_LIMITS_PAGE_TRANSLATIONS } from '@/pages/supplies-management/children/warehouses-limits/_constants/translations';
import { COMMON_TRANSLATIONS } from '@/pages/supplies-management/_constants/translations';
import { getDummyCoefficients } from './_utils/get-dummy-coefficients';
import { getLargestDataOfDates } from './_utils/get-largest-data-of-dates';
import { TableCellItem } from './_components/table-cell-item';
import { getFormattedTitleDate } from './_utils/get-formatted-title-date';
import styles from './index.module.scss';

const cn = classnames.bind(styles);
const BLOCK_NAME = 'Limits-table';

type TProps = {
  coefficientsReportData?: TConvertedCoefficientsTypeReport | null;
  selectedDate: Date | null;
  selectedWarehouse: TSearchMultiInputOptionsArray | null;
  costForPalette: number;
};

export const LimitsTable = memo(
  ({
    coefficientsReportData,
    selectedDate,
    selectedWarehouse,
    costForPalette,
  }: TProps) => {
    const warehousesData = useMemo(() => {
      const coefficients = Object.values(coefficientsReportData || {});
      if (coefficients.length) return coefficients;

      return getDummyCoefficients({
        selectedDate,
        selectedWarehouses: selectedWarehouse,
      });
    }, [coefficientsReportData, selectedDate, selectedWarehouse]);

    const largestDataOfDates = useMemo(() => {
      return getLargestDataOfDates({ warehousesData, selectedDate });
    }, [selectedDate, warehousesData]);

    const warehousesTableList = useMemo(() => {
      const warehousesKeys = Object.keys(coefficientsReportData || {});
      if (warehousesKeys.length) return warehousesKeys;

      return selectedWarehouse.map((el) => el.label);
    }, [coefficientsReportData, selectedWarehouse]);

    return (
      <div className={cn(BLOCK_NAME)}>
        <div className={cn(`${BLOCK_NAME}__warehouse-cell`)}>
          <div
            className={cn(
              `${BLOCK_NAME}__header-cell`,
              `${BLOCK_NAME}__header-cell--names`,
            )}
          >
            <Text
              size="h5-bold"
              text={i18next.t(WAREHOUSES_LIMITS_PAGE_TRANSLATIONS.warehouse)}
            />
          </div>
          <div
            className={cn(
              `${BLOCK_NAME}__header-cell`,
              `${BLOCK_NAME}__header-cell--names`,
            )}
          >
            <Text
              size="h5-bold"
              text={i18next.t(COMMON_TRANSLATIONS.supplyType)}
            />
          </div>
          {warehousesTableList.map((warehouseName, index) => (
            <div key={index} className={cn(`${BLOCK_NAME}__warehouse-block`)}>
              <div className={cn(`${BLOCK_NAME}__warehouse-item`)}>
                <Text
                  size="h4-bold"
                  text={warehouseName}
                  whiteSpace="pre-wrap"
                />
              </div>
              <div className={cn(`${BLOCK_NAME}__warehouse-type`)}>
                <div className={cn(`${BLOCK_NAME}__cell`)}>
                  <Text
                    size="h4-bold"
                    text={i18next.t(
                      WAREHOUSES_LIMITS_PAGE_TRANSLATIONS.boxesAmount,
                    )}
                    whiteSpace="pre-wrap"
                  />
                </div>
                <div className={cn(`${BLOCK_NAME}__cell`)}>
                  <Text
                    size="h4-bold"
                    text={i18next.t(
                      WAREHOUSES_LIMITS_PAGE_TRANSLATIONS.palettesAmount,
                    )}
                    whiteSpace="pre-wrap"
                  />
                </div>
                <div className={cn(`${BLOCK_NAME}__cell`)}>
                  <Text
                    size="h4-bold"
                    text={i18next.t(
                      WAREHOUSES_LIMITS_PAGE_TRANSLATIONS.superSafeAmount,
                    )}
                    whiteSpace="pre-wrap"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className={cn(`${BLOCK_NAME}__table-container`)}>
          <div className={cn(`${BLOCK_NAME}__table`)}>
            <div className={cn(`${BLOCK_NAME}__table-header`)}>
              {largestDataOfDates.map(({ date }) => (
                <div key={date} className={cn(`${BLOCK_NAME}__header-cell`)}>
                  <Text size="h5-bold" text={getFormattedTitleDate(date)} />
                </div>
              ))}
            </div>

            {warehousesData.map((el, index) => (
              <div key={index} className={cn(`${BLOCK_NAME}__table-body`)}>
                <div className={cn(`${BLOCK_NAME}__table-row`)}>
                  <TableCellItem
                    item={el.boxes}
                    maxLengthOfData={largestDataOfDates.length}
                  />
                </div>
                <div className={cn(`${BLOCK_NAME}__table-row`)}>
                  <TableCellItem
                    costForPalette={costForPalette}
                    isPalette
                    item={el.palette}
                    maxLengthOfData={largestDataOfDates.length}
                  />
                </div>
                <div className={cn(`${BLOCK_NAME}__table-row`)}>
                  <TableCellItem
                    item={el.superSafe}
                    maxLengthOfData={largestDataOfDates.length}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  },
);
